/**
 * Phase J.2 Pass 3 — Refund modal (super-admin only).
 *
 * Renders inside an invoice detail page next to a succeeded /
 * partially-refunded payment row. Caller passes the payment dict;
 * the modal pre-fills amount with the remaining refundable balance
 * and POSTs to /admin/payments/{id}/refunds on submit.
 */
import React, { useEffect, useState } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, AlertTriangle, RotateCcw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const REASONS = [
    { value: "duplicate", label: "Duplicate payment" },
    { value: "fraudulent", label: "Fraudulent / disputed" },
    { value: "requested_by_customer", label: "Customer requested" },
    { value: "other", label: "Other" },
];

function fmtMoney(cents) {
    if (cents == null) return "—";
    const v = Math.abs(cents) / 100;
    return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function RefundModal({ payment, open, onOpenChange, onIssued }) {
    const remaining = payment
        ? Math.max(0, (payment.amount_cents || 0) - (payment.refunded_cents_total || 0))
        : 0;
    const [amountDollars, setAmountDollars] = useState("");
    const [reason, setReason] = useState("requested_by_customer");
    const [note, setNote] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open && payment) {
            setAmountDollars((remaining / 100).toFixed(2));
            setReason("requested_by_customer");
            setNote("");
            setError(null);
        }
    }, [open, payment?.id, remaining]);

    if (!payment) return null;

    const amountCents = Math.round(parseFloat(amountDollars || "0") * 100);
    const validAmount = amountCents > 0 && amountCents <= remaining;
    const afterRefund = (payment.refunded_cents_total || 0) + amountCents;

    const submit = async () => {
        if (submitting) return;
        setError(null);
        if (!validAmount) {
            setError(`Amount must be greater than 0 and at most ${fmtMoney(remaining)}.`);
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post(
                `/admin/payments/${payment.id}/refunds`,
                { amount_cents: amountCents, reason, note: note || null },
            );
            toast.success(`Refund issued — ${fmtMoney(data.refund.amount_cents)}`);
            onOpenChange(false);
            onIssued && onIssued(data);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[480px]" data-testid="refund-modal">
                <DialogHeader>
                    <DialogTitle className="font-display text-xl flex items-center gap-2">
                        <RotateCcw size={18} className="text-purple-900" />
                        Issue refund
                    </DialogTitle>
                    <DialogDescription>
                        Stripe ACH refund will be initiated immediately. The customer
                        will be emailed automatically.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="rounded-md bg-slate-50 ring-1 ring-slate-200 p-3 text-sm space-y-1">
                        <div className="flex justify-between">
                            <span className="text-slate-500">Paid amount</span>
                            <span className="font-semibold text-slate-900 tabular-nums">{fmtMoney(payment.amount_cents)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500">Already refunded</span>
                            <span className="font-semibold text-slate-900 tabular-nums">{fmtMoney(payment.refunded_cents_total || 0)}</span>
                        </div>
                        <div className="flex justify-between border-t border-slate-200 pt-1 mt-1">
                            <span className="text-slate-500">Refundable</span>
                            <span className="font-semibold text-emerald-700 tabular-nums">{fmtMoney(remaining)}</span>
                        </div>
                    </div>

                    <div>
                        <label className="text-xs font-medium text-slate-700 uppercase tracking-wider" htmlFor="refund-amount">
                            Refund amount
                        </label>
                        <div className="relative mt-1">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                            <Input
                                id="refund-amount"
                                type="number" min="0.01" step="0.01"
                                max={(remaining / 100).toFixed(2)}
                                value={amountDollars}
                                onChange={(e) => setAmountDollars(e.target.value)}
                                className="pl-7"
                                data-testid="refund-amount-input"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="text-xs font-medium text-slate-700 uppercase tracking-wider">
                            Reason
                        </label>
                        <Select value={reason} onValueChange={setReason}>
                            <SelectTrigger className="mt-1" data-testid="refund-reason-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {REASONS.map((r) => (
                                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <label className="text-xs font-medium text-slate-700 uppercase tracking-wider" htmlFor="refund-note">
                            Internal note (optional)
                        </label>
                        <Textarea
                            id="refund-note"
                            value={note}
                            onChange={(e) => setNote(e.target.value.slice(0, 500))}
                            placeholder="Audit-trail note visible to admins only"
                            rows={2}
                            className="mt-1"
                            data-testid="refund-note-input"
                        />
                        <p className="text-[10px] text-slate-400 mt-0.5">{note.length}/500</p>
                    </div>

                    <div className="rounded-md bg-purple-50 ring-1 ring-purple-200 p-3 text-sm text-purple-900"
                         data-testid="refund-impact-preview">
                        After this refund, <strong>{fmtMoney(afterRefund)}</strong> will have been
                        refunded out of <strong>{fmtMoney(payment.amount_cents)}</strong>
                        {afterRefund >= (payment.amount_cents || 0)
                            ? <> — invoice will be marked <strong>refunded</strong>.</>
                            : <> — invoice will remain <strong>paid</strong> (partial refund logged).</>
                        }
                    </div>

                    {error && (
                        <div className="rounded-md bg-red-50 ring-1 ring-red-200 p-3 text-sm text-red-800 flex items-start gap-2"
                             data-testid="refund-error">
                            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}
                </div>

                <DialogFooter className="gap-2 pt-2">
                    <Button
                        type="button" variant="ghost"
                        disabled={submitting}
                        onClick={() => onOpenChange(false)}
                        data-testid="refund-cancel"
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        onClick={submit}
                        disabled={!validAmount || submitting}
                        className="bg-purple-900 hover:bg-purple-800 gap-2 min-w-[160px]"
                        data-testid="refund-submit"
                    >
                        {submitting
                            ? <><Loader2 size={14} className="animate-spin" /> Issuing…</>
                            : <>Issue refund {fmtMoney(amountCents)}</>}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
